import { LogLevel, type Configuration, type PopupRequest } from '@azure/msal-browser';

// Entra ID configuration. Every value comes from build-time env so the same
// bundle can be pointed at a different directory without a code change.
// Nothing secret lives here — a SPA is a public client and has no secret.

function required(name: string, value: string | undefined): string {
  if (!value) {
    throw new Error(
      `Missing ${name}. Copy .env.example to .env.local and fill it in.`
    );
  }
  return value;
}

const clientId = required('VITE_ENTRA_CLIENT_ID', import.meta.env.VITE_ENTRA_CLIENT_ID);
const tenantId = required('VITE_ENTRA_TENANT_ID', import.meta.env.VITE_ENTRA_TENANT_ID);

/**
 * Host of the directory that signs our users in.
 *
 * Customers sign in through a Microsoft Entra External ID (CIAM) directory,
 * which lives on its own host — `acutidy.ciamlogin.com` — not on the
 * workforce host. The tenant id alone does not identify it. Defaults to the
 * workforce host so a directory that never sets this keeps working.
 *
 * Always the tenant GUID after the host, never the <name>.onmicrosoft.com
 * form: MSAL rejects the domain-name spelling of an External ID authority.
 */
const authorityHost =
  import.meta.env.VITE_ENTRA_AUTHORITY_HOST || 'login.microsoftonline.com';

const isWorkforceHost = authorityHost === 'login.microsoftonline.com';

/**
 * The scope the API expects. Requesting this (rather than a Graph scope) is
 * what makes Entra mint an access token audienced to our own API — a token
 * for anything else is rejected by the API's audience check.
 */
export const apiScope = required('VITE_API_SCOPE', import.meta.env.VITE_API_SCOPE);

export const msalConfig: Configuration = {
  auth: {
    clientId,
    authority: `https://${authorityHost}/${tenantId}`,
    // MSAL refuses to talk to an authority host it does not already trust, and
    // it only trusts Microsoft's workforce hosts out of the box. Without this,
    // sign-in against ciamlogin.com fails before a request is ever made, with
    // an "untrusted authority" error that reads like a misconfigured tenant
    // rather than a missing allowlist entry.
    knownAuthorities: isWorkforceHost ? [] : [authorityHost],
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
    // Without this, MSAL leaves the app sitting on a blank page at the
    // redirect URI after login instead of returning to where the user was.
    navigateToLoginRequestUrl: true,
  },
  cache: {
    // sessionStorage over localStorage: this app holds a token that can write
    // to a customer's ERP, and a token that dies with the tab is the safer
    // default on a machine that may be shared.
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      logLevel: LogLevel.Warning,
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        if (level === LogLevel.Error) console.error(message);
        else if (level === LogLevel.Warning) console.warn(message);
      },
    },
  },
};

export const loginRequest: PopupRequest = {
  scopes: [apiScope],
};

/**
 * Straight to "create an account", skipping the sign-in form.
 *
 * ⚠ `prompt: 'create'` is a real OIDC value that Entra External ID honours —
 * not a hint. It opens the sign-up experience directly.
 *
 * Why it matters here: walking the sign-up path on 14 September 2026 showed
 * that BOTH marketing buttons — "Create an account" and "Sign in" — landed on
 * the same sign-in form, where the only way to register is a deliberately
 * understated "No account? Create one" link under the email box. Someone who
 * clicked a button labelled "Create an account" then had to find a smaller,
 * quieter link to do the thing the button promised.
 *
 * The alternative was restyling that link through Entra company branding CSS.
 * This is better: a customer who wants to sign up never sees the sign-in form
 * at all, and it uses a documented parameter instead of fighting somebody
 * else's stylesheet on every future update to their page.
 */
export const signUpRequest: PopupRequest = {
  scopes: [apiScope],
  prompt: 'create',
};
